import { useEffect, useState } from 'react'
import { useDelayRender } from 'remotion'
import { loadWaveformPeak } from './waveformPeakLoader'

/**
 * The whole-file audio peak for `url`, held behind a `delayRender` so a Lambda
 * frame is not captured before it is known.
 *
 * Three states, and the distinction matters to the caller:
 *
 * - `undefined` — still loading. `useFrequencyData` renders nothing rather than
 *   flashing one frame on the window-local scale before the real one arrives.
 * - `null` — there is no peak (no url, a 404, an expired signed URL, a silent
 *   mix). The caller falls back to the window-local scale.
 * - `number` — the peak.
 *
 * Failure is `null` and `continueRender`, never `cancelRender`: an unreachable
 * `waveform.dat` must not fail a render over the difference between one
 * amplitude scale and another.
 */
export function useWaveformPeak(
  url: string | null | undefined,
): number | null | undefined {
  const { delayRender, continueRender } = useDelayRender()
  const [peak, setPeak] = useState<number | null | undefined>(
    url ? undefined : null,
  )

  useEffect(() => {
    if (!url) {
      setPeak(null)
      return
    }

    let cancelled = false
    setPeak(undefined)

    const handle = delayRender(`Loading waveform peak from "${url}"`)

    loadWaveformPeak(url)
      .then((value) => {
        if (!cancelled) setPeak(value)
      })
      .finally(() => {
        continueRender(handle)
      })

    return () => {
      cancelled = true
      continueRender(handle)
    }
  }, [url, delayRender, continueRender])

  return peak
}
